import { Box } from "@mui/material"
import type { FC } from "react"
import { useFormContext } from "react-hook-form"

import { StepSectionHeader } from "./StepSectionHeader"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { FormMultiAutocomplete } from "@/components/UiKit/FormControls/FormMultiAutocomplete"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import type { Product } from "@/graphql/codegen/graphql"
import useAssociateProductColumns from "@/screens/Products/hooks/useAssociateProductColumns"
import type { AssociateProductFormValues } from "@/screens/Products/schemas/associateProductForm"

type CustomerInfoFields = Pick<AssociateProductFormValues, "customerCode" | "customerDescription">

interface AssociateProductCustomerInfoStepProps {
  product: Product
  disabled?: boolean
  sectionTwoTitle?: string
}

export const AssociateProductCustomerInfoStep: FC<AssociateProductCustomerInfoStepProps> = ({
  product,
  disabled = false,
  sectionTwoTitle,
}) => {
  const { control } = useFormContext<CustomerInfoFields>()
  const columns = useAssociateProductColumns({ showRadioButton: false })

  return (
    <Box className='flex flex-col gap-10'>
      <Box className='flex flex-col'>
        <StepSectionHeader title='1. Selected Product' complete />
        <AerosBaseTable
          features={{
            tanstackOptions: {
              data: [product],
              columns,
              getRowId: (row) => row.productId.toString(),
            },
          }}
        />
      </Box>

      <Box className='flex flex-col'>
        <StepSectionHeader
          title={
            sectionTwoTitle ??
            (disabled ? "2. Customer's Default Product Info" : "2. Add Customer's Default Product Info")
          }
          complete={disabled}
        />
        <Box className='grid grid-cols-2 gap-4'>
          <FormMultiAutocomplete
            control={control}
            name='customerCode'
            label='Customer Code'
            options={[]}
            freeSolo
            placeholder='Type and press Enter'
            disabled={disabled}
            data-testid='associate-product-customer-code'
          />
          <FormTextField
            control={control}
            name='customerDescription'
            label='Customer Description'
            placeholder='Add Description'
            disabled={disabled}
            data-testid='associate-product-customer-description'
          />
        </Box>
      </Box>
    </Box>
  )
}
